import { supabase } from '../../supabase/client';
import { getClubLogoStoragePath } from '../clubLogo';

/**
 * Upload a file to Supabase Storage
 * @param {File|Blob} file - The file to upload
 * @param {string} bucketName - The storage bucket name
 * @param {string} fileName - The file name in storage (always ClubLogo.png for club logos)
 * @param {string} path - Optional path within the bucket
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const uploadToSupabaseStorage = async (file, bucketName, fileName, path = '') => {
  try {
    // Construct the full file path
    const fullPath = path ? `${path}/${fileName}` : fileName;

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(fullPath, file, {
        cacheControl: '3600',
        upsert: true // This allows overwriting existing files
      });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    // Get the public URL for the uploaded file
    const { data: urlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(fullPath);

    return {
      success: true,
      data: {
        path: data.path,
        publicUrl: urlData.publicUrl,
        fullPath: fullPath
      }
    };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Convert Base64 string to File object
 * @param {string} base64String - The Base64 string
 * @param {string} fileName - The file name
 * @param {string} mimeType - The MIME type
 * @returns {File}
 */
export const base64ToFile = (base64String, fileName, mimeType) => {
  // Convert Base64 to binary
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  // Create Blob and then File
  const blob = new Blob([bytes], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
};

/**
 * Upload club logo to Supabase Storage
 * @param {string} base64Logo - The Base64 string of the logo
 * @param {string} mimeType - The MIME type (e.g., 'image/png', 'image/jpeg')
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const uploadClubLogo = async (base64Logo, clubID, mimeType = 'image/png') => {
  try {
    const storagePath = getClubLogoStoragePath(clubID);
    if (!storagePath) {
      return { success: false, error: 'clubID is required for club logo upload' };
    }

    const logoFile = base64ToFile(base64Logo, 'ClubLogo.png', mimeType);
    const folder = String(clubID);

    const result = await uploadToSupabaseStorage(
      logoFile,
      'ClubLogo',
      'ClubLogo.png',
      folder
    );

    return result;
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete a file from Supabase Storage
 * @param {string} bucketName - The storage bucket name
 * @param {string} fileName - The file name to delete
 * @param {string} path - Optional path within the bucket
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteFromSupabaseStorage = async (bucketName, fileName, path = '') => {
  try {
    const fullPath = path ? `${path}/${fileName}` : fileName;
    
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([fullPath]);

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    return { success: true };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Upload a user profile image to Supabase Storage
 * @param {string} base64Image - Base64 encoded image data
 * @param {string} studentID - Student ID for unique filename
 * @param {string} mimeType - The MIME type (e.g., 'image/png', 'image/jpeg')
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const uploadUserProfile = async (base64Image, studentID, mimeType = 'image/png') => {
  try {
    // Convert Base64 to File
    const profileFile = base64ToFile(base64Image, `${studentID}.png`, mimeType);

    // Upload to profile-images bucket (directly in bucket root)
    const { data, error } = await supabase.storage
      .from('profile-images')
      .upload(`${studentID}.png`, profileFile, {
        cacheControl: '3600',
        upsert: true // This allows overwriting existing files
      });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    // Get the public URL for the uploaded file
    const { data: urlData } = supabase.storage
      .from('profile-images')
      .getPublicUrl(`${studentID}.png`);

    return {
      success: true,
      data: {
        publicUrl: urlData.publicUrl,
        fileName: `${studentID}.png`
      }
    };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Upload a user profile image during signup (unauthenticated)
 * Uses service role to bypass RLS policies
 * @param {string} base64Image - Base64 encoded image data
 * @param {string} studentID - Student ID for unique filename
 * @param {string} mimeType - The MIME type (e.g., 'image/png', 'image/jpeg')
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const uploadUserProfileSignup = async (base64Image, studentID, mimeType = 'image/png') => {
  try {
    // Use regular supabase client with public RLS policies
    const supabaseAdmin = supabase; // Use the regular client

    // Convert Base64 to File
    const profileFile = base64ToFile(base64Image, `${studentID}.png`, mimeType);

    // Upload to profile-images bucket using public RLS policies
    const { data, error } = await supabaseAdmin.storage
      .from('profile-images')
      .upload(`${studentID}.png`, profileFile, {
        cacheControl: '3600',
        upsert: true // This allows overwriting existing files
      });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    // Get the public URL for the uploaded file
    const { data: urlData } = supabaseAdmin.storage
      .from('profile-images')
      .getPublicUrl(`${studentID}.png`);

    return {
      success: true,
      data: {
        publicUrl: urlData.publicUrl,
        fileName: `${studentID}.png`
      }
    };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};
